import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useCallback } from 'react';
import { Weekday, weekdayToNumber } from './weekdays';
import { Month, monthToNumber } from './months';

export const useGetScheduleHint = () => {
  const { formatDateTimeWithTimeZone, formatDateWithTimeZone } =
    useDateTimeFormat();

  const getScheduleHint = useCallback(
    (
      interval: string,
      intervalStart: string,
      endDate: Date,
      executionTime: string
    ) => {
      if (!interval || !endDate) {
        return null;
      }

      const millisecondsInADay = 24 * 60 * 60 * 1000;
      const millisecondsInAMinute = 60 * 1000;

      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      let endTimestamp = 0;

      switch (interval) {
        case 'daily':
          endTimestamp = startDate.getTime() + millisecondsInADay;
          break;
        case 'weekly': {
          const targetDay = weekdayToNumber(intervalStart as Weekday);
          const currentDay = startDate.getDay() === 0 ? 7 : startDate.getDay(); // Convert Sunday (0) to 7
          const dayOffset = targetDay - currentDay;

          startDate.setDate(startDate.getDate() + dayOffset);

          if (dayOffset > 0) {
            startDate.setDate(startDate.getDate() - 7);
          }

          endTimestamp = startDate.getTime() + 7 * millisecondsInADay;
          break;
        }
        case 'monthly': {
          startDate.setDate(1);
          const nextMonth = new Date(startDate);
          nextMonth.setMonth(startDate.getMonth() + 1);
          endTimestamp = nextMonth.getTime();
          break;
        }
        case 'quarterly': {
          startDate.setDate(1);
          const currentMonth = startDate.getMonth();
          const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
          startDate.setMonth(quarterStartMonth);
          const nextQuarter = new Date(startDate);
          nextQuarter.setMonth(startDate.getMonth() + 3);
          endTimestamp = nextQuarter.getTime();
          break;
        }
        case 'yearly': {
          const targetMonth = monthToNumber(intervalStart as Month) - 1; // Convert to 0-based index
          startDate.setDate(1);
          startDate.setMonth(targetMonth);
          if (startDate < new Date()) {
            startDate.setFullYear(startDate.getFullYear() + 1);
          }
          const nextYear = new Date(startDate);
          nextYear.setFullYear(startDate.getFullYear() + 1);
          endTimestamp = nextYear.getTime();
          break;
        }
        default:
          return null;
      }

      const processDate = new Date(endTimestamp);
      const endDateObj = new Date(endTimestamp - millisecondsInAMinute);

      const scheduleText = `Will run ${interval}${
        interval === 'weekly' && intervalStart ? ` (${intervalStart})` : ''
      } until ${formatDateWithTimeZone(new Date(endDate))}.`;

      const nextJobText =
        processDate > endDate
          ? 'All jobs are complete.'
          : `Next job will be processed ${formatDateTimeWithTimeZone(
              processDate
            )}, covering ${formatDateTimeWithTimeZone(
              startDate
            )} to ${formatDateTimeWithTimeZone(endDateObj)}.`;

      return {
        scheduleText,
        nextJobText,
      };
    },
    [formatDateTimeWithTimeZone, formatDateWithTimeZone]
  );

  return getScheduleHint;
};
